import { useEffect, useRef, useState } from 'react';
import env from '../../config/env';

import './AutocompleteMapInput.css';

function Icon({ name, className = '' }) {
  const paths = {
    'location-outline': (
      <>
        <path d="M12 21s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12Z" />
        <circle cx="12" cy="9" r="2.5" />
      </>
    ),

    'close-outline': (
      <>
        <path d="M6 6l12 12M18 6 6 18" />
      </>
    ),

    'search-outline': (
      <>
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m16 16 5 5" />
      </>
    ),

    'alert-circle-outline': (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5M12 16h.01" />
      </>
    ),

    'chevron-forward-outline': <path d="m9 6 6 6-6 6" />,
  };

  return (
    <svg
      className={`mc-icon ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name] || paths['location-outline']}
    </svg>
  );
}

function AddressAutocompleteInput({
  handleSelectLocation,
  resetInput,
  initialValue = '',
  placeholder = 'Entrez une adresse...',
}) {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [searchResults, setSearchResults] = useState([]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sessionToken, setSessionToken] = useState(() => crypto.randomUUID());

  const searchTimeoutRef = useRef(null);

  /*
   * RESET
   */
  useEffect(() => {
    if (resetInput) {
      setSearchTerm('');
      setSearchResults([]);
      setShow(false);
      setError(null);
    }
  }, [resetInput]);

  /*
   * ICON
   */
  const getIconName = () => {
    return 'location-outline';
  };

  /*
   * NORMALISATION
   */
  const normalizeAddress = (feature) => {
    const coordinates = feature?.geometry?.coordinates;

    if (
      !Array.isArray(coordinates) ||
      coordinates.length < 2 ||
      typeof coordinates[0] !== 'number' ||
      typeof coordinates[1] !== 'number'
    ) {
      return null;
    }

    const properties = feature?.properties || {};

    const address = properties.full_address || feature?.place_name || properties.name || '';

    return {
      properties: {
        full_address: address,

        coordinates: {
          longitude: coordinates[0],
          latitude: coordinates[1],
        },

        source: 'address',

        mapbox_id: feature?.id || null,

        feature_type: feature?.properties?.feature_type || null,
      },
    };
  };

  /*
   * SELECT ADDRESS
   */
  const handleSelectAddress = (suggestion) => {
    const address = suggestion?.properties?.full_address;

    if (!address) return;

    setSearchTerm(address);
    setSearchResults([]);
    setShow(false);

    handleSelectLocation({
      name: address,
      address,
      location: [
        suggestion.properties.coordinates.longitude,
        suggestion.properties.coordinates.latitude,
      ],
      mapbox_id: suggestion.properties.mapbox_id,
    });

    // Nouvelle session après sélection
    setSessionToken(crypto.randomUUID());
  };

  /*
   * SEARCH
   */
  const searchAddress = async (value) => {
    if (!value || value.trim().length < 2) {
      setSearchResults([]);
      setShow(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      /*
       * On demande EXCLUSIVEMENT des adresses.
       *
       * Le backend doit transmettre :
       * types=address
       */
      const url =
        `${env.baseUrlApi}/mapbox/geocoding` +
        `?q=${encodeURIComponent(value.trim())}` +
        `&limit=8` +
        `&types=address` +
        `&session_token=${sessionToken}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Erreur lors de la recherche');
      }

      const data = await response.json();

      const features = data?.features || [];

      const addresses = features.map(normalizeAddress).filter(Boolean);

      setSearchResults(addresses);
      setShow(true);
    } catch (err) {
      console.error('Address autocomplete error:', err);

      setError(err);
      setSearchResults([]);
      setShow(true);
    } finally {
      setLoading(false);
    }
  };

  /*
   * INPUT CHANGE
   */
  const handleInputChange = (event) => {
    const value = event.target.value;

    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.trim().length < 2) {
      setSearchResults([]);
      setShow(false);
      setError(null);
      return;
    }

    /*
     * Petit debounce pour éviter de faire une requête
     * à chaque caractère.
     */
    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(value);
    }, 300);
  };

  /*
   * CLEAR
   */
  const handleClearInput = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShow(false);
    setError(null);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  /*
   * CLEANUP
   */
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="mc-address-search">
      <div className="mc-address-input-wrapper">
        <span className="mc-address-icon">
          <Icon name="location-outline" />
        </span>

        <input
          type="text"
          id="addressInput"
          name="address"
          className="mc-address-input"
          value={searchTerm}
          onChange={handleInputChange}
          placeholder={placeholder}
          autoComplete="off"
          required
          onFocus={() => {
            if (searchTerm.length >= 2) {
              setShow(true);
            }
          }}
          onBlur={() => {
            setTimeout(() => setShow(false), 200);
          }}
        />

        {searchTerm && (
          <button
            type="button"
            className="mc-address-clear"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClearInput}
            aria-label="Effacer l'adresse"
          >
            <Icon name="close-outline" />
          </button>
        )}
      </div>

      {searchTerm.length >= 2 && show && (
        <div className="mc-address-results">
          {loading && (
            <div className="mc-address-state">
              <span className="mc-address-spinner" />
              <span>Recherche de l'adresse...</span>
            </div>
          )}

          {!loading && error && (
            <div className="mc-address-state mc-address-error">
              <Icon name="alert-circle-outline" />
              <span>Impossible de rechercher cette adresse</span>
            </div>
          )}

          {!loading && !error && searchResults.length === 0 && (
            <div className="mc-address-state">
              <Icon name="search-outline" />
              <span>Aucune adresse trouvée</span>
            </div>
          )}

          {!loading && !error && searchResults.length > 0 && (
            <div className="mc-address-result-list">
              {searchResults.map((suggestion, index) => (
                <button
                  key={suggestion.properties.mapbox_id || index}
                  type="button"
                  className="mc-address-result"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelectAddress(suggestion)}
                >
                  <span className="mc-address-result-icon">
                    <Icon name={getIconName(suggestion)} />
                  </span>

                  <span className="mc-address-result-content">
                    <span className="mc-address-result-title">
                      {suggestion.properties.full_address}
                    </span>
                  </span>

                  <Icon className="mc-address-result-arrow" name="chevron-forward-outline" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AddressAutocompleteInput;
